// Story 1.26 — TanStack Query hook for GET /api/v1/creators/me/dashboard.
//
// Single endpoint, single envelope. The response carries 3 panels
// (applications, earnings, dossiers); each is either Wired (has
// `data`) or Unavailable (has `unavailable_reason` +
// `populates_in_story`). The SPA narrows on `"data" in panel` to
// branch render arms.
//
// Types are RE-DECLARED locally (not cross-imported from the admin
// dashboard hook) — feature-folder isolation. Wire shape is identical
// so the two surfaces could share a discriminated union if desired,
// but the cost of importing is higher than the cost of duplicating
// these ~50 lines.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { PortfolioApplicationStatus } from "../../lib/portfolio-status-badge";

/**
 * Discriminated union: a panel is EITHER wired (has `data`) OR
 * unavailable (has `unavailable_reason` + `populates_in_story`).
 * Narrow via `"data" in panel` / `"unavailable_reason" in panel`.
 */
export type CreatorPanelEnvelope<T> =
  | { data: T }
  | { unavailable_reason: string; populates_in_story: string };

export type CreatorApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn";

export type CreatorApplicationSummary = {
  application_id: string;
  status: CreatorApplicationStatus;
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  can_resubmit: boolean;
};

export type PortfolioApplicationSummary = {
  id: string;
  portfolio_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  status: PortfolioApplicationStatus;
  submitted_at: string;
  reviewed_at: string | null;
};

export type ApplicationsPanelData = {
  creator_application: CreatorApplicationSummary | null;
  portfolio_applications: PortfolioApplicationSummary[];
};

export type EarningsPanelData = {
  cumulative_eur_cents: number;
  current_month_eur_cents: number;
  primary_eur_cents: number;
  resale_eur_cents: number;
  holders_count: number | null;
  sparkline_4w: number[];
  period_start: string | null;
};

export type DropStatus = "scheduled" | "live" | "retiring" | "retired";

/** One dossier (drop) this creator has been revealed from. */
export type CreatorDossierRow = {
  drop_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  drop_status: DropStatus;
  /** Times a dossier from this drop was opened and revealed this creator. */
  opens_count: number;
  /** Distinct current holders of a live certificate from this drop. */
  holder_count: number;
  first_appeared_at: string | null;
};

export type DossiersPanelData = {
  dossiers: CreatorDossierRow[];
  total_opens: number;
  /** Distinct current holders across ALL drops (not the sum of per-drop). */
  total_holders: number;
};

export type CreatorDashboardEnvelope = {
  generated_at: string;
  applications: CreatorPanelEnvelope<ApplicationsPanelData>;
  earnings: CreatorPanelEnvelope<EarningsPanelData>;
  dossiers: CreatorPanelEnvelope<DossiersPanelData>;
};

export type GetCreatorDashboardResponse = {
  data: CreatorDashboardEnvelope;
};

async function fetchCreatorDashboard(): Promise<GetCreatorDashboardResponse> {
  const response = await fetch("/api/v1/creators/me/dashboard", {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Creator dashboard fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as GetCreatorDashboardResponse;
}

export function useCreatorDashboard() {
  return useQuery<GetCreatorDashboardResponse, Error>({
    queryKey: ["creator-dashboard"],
    queryFn: fetchCreatorDashboard,
    staleTime: 60_000, // 1 min — same as Story 1.29's admin metrics
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: false, // EXPLICIT — no auto-polling
  });
}
